import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

const FILE_DOWNLOADS_ENABLED_KEY = 'file_downloads_enabled';
const ANDROID_PLATFORM = 'android';

export type FileDownloadPermissionState = 'prompt' | 'prompt-with-rationale' | 'granted' | 'denied' | 'unavailable';

export type FileDownloadResult = {
    mode: 'web';
} | {
    mode: 'native';
    savedPath: string;
    savedUri: string;
    openedAutomatically: boolean;
};

function sanitizeFileName(fileName: string): string {
    const trimmedName = fileName.trim();
    const fallbackName = trimmedName !== '' ? trimmedName : 'archivo';
    const sanitizedName = fallbackName.replace(/[^\w.-]+/g, '_');

    return sanitizedName !== '' ? sanitizedName : 'archivo';
}

function toNativeViewerPathCandidates(savedUri: string, savedPath: string): string[] {
    const candidates = new Set<string>();

    if (savedUri.trim() !== '') {
        candidates.add(savedUri);
    }

    if (savedUri.startsWith('file://')) {
        const withoutFileScheme = savedUri.slice('file://'.length);
        if (withoutFileScheme.trim() !== '') {
            candidates.add(withoutFileScheme);
        }
    }

    if (savedPath.startsWith('Download/')) {
        candidates.add(`/storage/emulated/0/${savedPath}`);
    }

    return Array.from(candidates);
}

function splitNameAndExtension(fileName: string): { baseName: string; extension: string } {
    const trimmedName = fileName.trim();
    const lastDotIndex = trimmedName.lastIndexOf('.');

    if (lastDotIndex <= 0 || lastDotIndex === trimmedName.length - 1) {
        return {
            baseName: trimmedName,
            extension: '',
        };
    }

    return {
        baseName: trimmedName.slice(0, lastDotIndex),
        extension: trimmedName.slice(lastDotIndex),
    };
}

async function resolveAvailableFileName(
    stat: (path: string) => Promise<void>,
    desiredFileName: string
): Promise<string> {
    const { baseName, extension } = splitNameAndExtension(desiredFileName);

    let counter = 1;
    let candidateName = desiredFileName;

    while (true) {
        try {
            await stat(candidateName);
            counter += 1;
            candidateName = `${baseName} (${counter})${extension}`;
        } catch {
            return candidateName;
        }
    }
}

function blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = () => {
            if (typeof reader.result !== 'string') {
                reject(new Error('No se pudo convertir el archivo a base64.'));
                return;
            }

            const commaIndex = reader.result.indexOf(',');

            if (commaIndex === -1) {
                reject(new Error('Formato de base64 inválido para descarga nativa.'));
                return;
            }

            resolve(reader.result.slice(commaIndex + 1));
        };

        reader.onerror = () => {
            reject(new Error('No se pudo leer el blob para descarga nativa.'));
        };

        reader.readAsDataURL(blob);
    });
}

async function downloadOnWeb(blob: Blob, fileName: string): Promise<FileDownloadResult> {
    const objectUrl = URL.createObjectURL(blob);
    const downloadAnchor = document.createElement('a');

    downloadAnchor.href = objectUrl;
    downloadAnchor.download = fileName;
    downloadAnchor.rel = 'noopener noreferrer';

    document.body.appendChild(downloadAnchor);
    downloadAnchor.click();
    downloadAnchor.remove();

    setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);

    return {
        mode: 'web',
    };
}

export function isNativeFileDownloadSupported(): boolean {
    return Capacitor.isNativePlatform();
}

export async function getFileDownloadsEnabled(): Promise<boolean> {
    const { value } = await Preferences.get({ key: FILE_DOWNLOADS_ENABLED_KEY });
    return value !== 'false';
}

export async function setFileDownloadsEnabled(enabled: boolean): Promise<void> {
    await Preferences.set({ key: FILE_DOWNLOADS_ENABLED_KEY, value: enabled ? 'true' : 'false' });
}

export async function getFileDownloadPermissionState(): Promise<FileDownloadPermissionState> {
    if (!isNativeFileDownloadSupported()) {
        return 'unavailable';
    }

    if (Capacitor.getPlatform() !== ANDROID_PLATFORM) {
        return 'granted';
    }

    const { Filesystem } = await import('@capacitor/filesystem');
    const status = await Filesystem.checkPermissions();

    return status.publicStorage;
}

export async function requestFileDownloadPermissionFromSettings(): Promise<'granted' | 'denied'> {
    if (!isNativeFileDownloadSupported()) {
        return 'denied';
    }

    if (Capacitor.getPlatform() !== ANDROID_PLATFORM) {
        return 'granted';
    }

    const { Filesystem } = await import('@capacitor/filesystem');
    const requested = await Filesystem.requestPermissions();

    return requested.publicStorage === 'granted' ? 'granted' : 'denied';
}

export async function initializeFileDownloadAccess(): Promise<void> {
    if (!isNativeFileDownloadSupported()) {
        return;
    }

    const enabled = await getFileDownloadsEnabled();
    if (!enabled) {
        return;
    }

    const permissionStatus = await getFileDownloadPermissionState();
    if (permissionStatus === 'granted' || permissionStatus === 'denied') {
        return;
    }

    await requestFileDownloadPermissionFromSettings();
}

async function ensureFileDownloadPermission(): Promise<'granted' | 'denied'> {
    const status = await getFileDownloadPermissionState();

    if (status === 'granted') {
        return 'granted';
    }

    if (status === 'denied' || status === 'unavailable') {
        return 'denied';
    }

    return requestFileDownloadPermissionFromSettings();
}

async function downloadOnNative(blob: Blob, fileName: string): Promise<FileDownloadResult> {
    const { Filesystem, Directory } = await import('@capacitor/filesystem');

    const downloadsEnabled = await getFileDownloadsEnabled();
    if (!downloadsEnabled) {
        throw new Error('Las descargas están desactivadas en Configuración.');
    }

    const permissionResult = await ensureFileDownloadPermission();
    if (permissionResult !== 'granted') {
        throw new Error('No se concedió permiso para guardar archivos en el dispositivo.');
    }

    const safeFileName = sanitizeFileName(fileName);
    const base64Data = await blobToBase64(blob);

    if (Capacitor.getPlatform() === ANDROID_PLATFORM) {
        const downloadsFileName = await resolveAvailableFileName(
            async (path) => {
                await Filesystem.stat({
                    path: `Download/${path}`,
                    directory: Directory.ExternalStorage,
                });
            },
            safeFileName
        );

        const downloadsPath = `Download/${downloadsFileName}`;

        try {
            await Filesystem.writeFile({
                path: downloadsPath,
                data: base64Data,
                directory: Directory.ExternalStorage,
                recursive: true,
            });

            const downloadsUri = await Filesystem.getUri({
                path: downloadsPath,
                directory: Directory.ExternalStorage,
            });
            const openedAutomatically = await openDownloadedFileOnNative(downloadsUri.uri, downloadsPath);

            return {
                mode: 'native',
                savedPath: downloadsPath,
                savedUri: downloadsUri.uri,
                openedAutomatically,
            };
        } catch {
            // Android 11+ puede bloquear ExternalStorage. Se usa fallback seguro en Documents.
        }
    }

    const uniquePath = await resolveAvailableFileName(
        async (path) => {
            await Filesystem.stat({
                path,
                directory: Directory.Documents,
            });
        },
        safeFileName
    );

    await Filesystem.writeFile({
        path: uniquePath,
        data: base64Data,
        directory: Directory.Documents,
        recursive: false,
    });

    const uriResult = await Filesystem.getUri({
        path: uniquePath,
        directory: Directory.Documents,
    });
    const openedAutomatically = await openDownloadedFileOnNative(uriResult.uri, uniquePath);

    return {
        mode: 'native',
        savedPath: uniquePath,
        savedUri: uriResult.uri,
        openedAutomatically,
    };
}

async function openDownloadedFileOnNative(savedUri: string, savedPath: string): Promise<boolean> {
    if (!Capacitor.isNativePlatform()) {
        return false;
    }

    try {
        const { FileViewer } = await import('@capacitor/file-viewer');
        const candidates = toNativeViewerPathCandidates(savedUri, savedPath);

        for (const path of candidates) {
            try {
                await FileViewer.openDocumentFromLocalPath({ path });
                return true;
            } catch {
                // Intentar siguiente formato de ruta/uri.
            }
        }
    } catch {
        // El plugin no está disponible o no pudo abrir el archivo.
    }

    return false;
}

export async function downloadBlobToDevice(blob: Blob, fileName: string): Promise<FileDownloadResult> {
    if (Capacitor.isNativePlatform()) {
        return downloadOnNative(blob, fileName);
    }

    return downloadOnWeb(blob, fileName);
}
